import React from 'react';
import { useUI } from '../contexts/UIProvider';
import { useRouterContext } from '../contexts/RouterProvider';
import { parseListingSort, withListingSort } from '../utils/listing-sort';
import { filtersFromQuery, hasActiveFilters } from '../utils/search-filter-state';
import Tooltip from './tooltip';

/**
 * Grid or showcase, for the search results below it.
 *
 * The classes are the stylesheet's, not Tailwind's: the utilities this markup
 * used to carry (`bg-sw-green`, `bg-sw-yellow`, `text-sw-grey-label`,
 * `border-sw-grey-line`) were defined in a tailwind.config.js that Tailwind v4
 * never loaded, so they emitted nothing at all. That file has since been
 * deleted. See search-view-toggle.scss.
 *
 * A filter forces the grid: rows cannot carry filters (SearchListing's
 * `filtered`), so Showcase is disabled, because dropping someone's filters on
 * a view switch would lose their work. A picked sort does not: the rows keep
 * their ranked order and each row's listings follow the sort. The buttons
 * show the view actually on screen, not the saved preference.
 */
const VIEWS = [
  {
    mode: 'grid' as const,
    id: 'search-view-grid',
    label: 'Grid view',
    path: 'M3 3h8v8H3V3zm0 10h8v8H3v-8zm10-10h8v8h-8V3zm0 10h8v8h-8v-8z',
  },
  {
    mode: 'showcase' as const,
    id: 'search-view-showcase',
    label: 'Showcase view',
    path: 'M2 4h20v4H2V4zm0 6h8v10H2V10zm10 0h10v4H12v-4zm0 6h10v4H12v-4z',
  },
];

interface SearchViewToggleProps {
  /**
   * A picked sort forces the grid and Showcase drops it: For You, whose rows
   * are its own personal order. Elsewhere the rows keep the sort, each row's
   * listings in that order (since 2026-10-14).
   */
  sortForcesGrid?: boolean;
}

export const SHOWCASE_BLOCKED_TIP = 'Clear the filters to see showcase rows';

const SearchViewToggle: React.FC<SearchViewToggleProps> = ({ sortForcesGrid = false }) => {
  const { searchViewMode, setSearchViewMode } = useUI();
  const { asPath, query, replace } = useRouterContext();
  const sort = sortForcesGrid ? parseListingSort(query?.sortBy) : undefined;
  // The router's query is always an object (empty on a page with none).
  const filtered = hasActiveFilters(filtersFromQuery(query as Record<string, never>));
  const shown = sort || filtered ? 'grid' : searchViewMode;

  const pick = (mode: 'grid' | 'showcase') => {
    setSearchViewMode(mode);
    if (mode === 'showcase' && sort) replace(withListingSort(asPath, undefined), { shallow: true });
  };

  // The product page's tooltips (components/tooltip.tsx), not the browser's
  // `title`, which shows late, unstyled, and not at all on a disabled button.
  // Showcase under a filter is aria-disabled rather than disabled for the same
  // reason: a disabled button gets no hover, so it could not say why.
  return (
    <div className="search-view-toggle xs:mr-3">
      {VIEWS.map(({ mode, id, label, path }) => {
        const active = shown === mode;
        const blocked = mode === 'showcase' && filtered;
        return (
          <button
            key={mode}
            id={id}
            type="button"
            onClick={() => { if (!blocked) pick(mode); }}
            aria-disabled={blocked || undefined}
            className={`search-view-toggle__button${active ? ' search-view-toggle__button--active' : ''}${blocked ? ' search-view-toggle__button--blocked' : ''}`}
            aria-label={label}
            aria-pressed={active}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d={path} />
            </svg>
          </button>
        );
      })}
      {VIEWS.map(({ mode, id, label }) => (
        <Tooltip
          key={`${mode}-tip`}
          position="above"
          triggerElement={`#${id}`}
          theme="dark"
          triggerEvent="hover"
          className="search-view-toggle__tip"
        >
          <span>{mode === 'showcase' && filtered ? SHOWCASE_BLOCKED_TIP : label}</span>
        </Tooltip>
      ))}
    </div>
  );
};

export default SearchViewToggle;
